import React, { useEffect, useMemo, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getSupplierCatalogue, getSupplierCatalogueProducts, saveSupplierCatalogue } from '../../services/supplier.service';
import { getGroupProduct } from '../../services/items.services';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  ConfirmDialog,
  DocumentHeader,
  EmptyState,
  ErrorBanner,
  Fact,
  FieldMessage,
  IconButton,
  LinesCard,
  MUTED,
  NumberField,
  PageFrame,
  PhoneLabel,
  PhoneLine,
  PickerRow,
  ProductCell,
  ProductPicker,
  SaveBar,
  SelectField,
  Skeleton,
  card,
  defaultUnitId,
  tableStyle,
  td,
  th,
  unitsOfProduct,
  useLanguage,
} from '../../components/purchasing';
import { parseNumber, priceText } from '../Order/orderLineRules';
import {
  CatalogueLine,
  baseUnitName,
  cataloguePayload,
  changesOf,
  checkLine,
  lineOfPick,
  lineOfRow,
  unitKey,
  withCost,
  withUnit,
  withUnitCost,
} from './catalogueLines';

const PICK_PAGE = 20;

/** A supplier's catalogue on one page: add many products at once, price each unit, save everything together. */
const SupplierInventoryDetail = () => {
  const preLanguage = localStorage.getItem('language');
  const companyId = localStorage.getItem('companyId') || '';
  const id: string = window.location.pathname.split('/').pop() || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [supplier, setSupplier] = useState<{ id: string; companyName: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [lines, setLines] = useState<CatalogueLine[]>([]);
  const [saved, setSaved] = useState<CatalogueLine[]>([]);
  const [flagged, setFlagged] = useState(false);
  const [lineSearch, setLineSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'' | 'leave'>('');

  // Add products
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [pickTerm, setPickTerm] = useState('');
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [groupId, setGroupId] = useState('');
  const [picks, setPicks] = useState<any[]>([]);
  const [pickCount, setPickCount] = useState(0);
  const [pickPage, setPickPage] = useState(1);
  const [picksLoading, setPicksLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, string>>(() => new Map());
  const pickSeq = useRef(0);
  const seenPicks = useRef(new Map<string, any>());

  const name = supplier?.companyName ?? '';

  /* ---------------- Loading ---------------- */

  const apply = (data: any) => {
    const next = (data?.lines ?? []).map(lineOfRow);
    setSupplier(data?.supplier ?? null);
    setLines(next);
    setSaved(next);
    setFlagged(false);
  };

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      apply((await getSupplierCatalogue(id))?.data?.data);
    } catch (error: any) {
      setFailed(true);
      showApiError(error, { language });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id === 'new') {
      // A catalogue belongs to a supplier: there is nothing to start from here.
      webNavigate(`/${preLanguage}/supplierInventory`);
      return;
    }
    load();
    const onToken = () => load();
    document.addEventListener('newToken', onToken);
    return () => document.removeEventListener('newToken', onToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- Lines ---------------- */

  const checks = useMemo(() => new Map(lines.map((line) => [line.key, checkLine(line, lines, flagged)])), [lines, flagged]);
  const changes = changesOf(lines, saved);
  const dirty = changes.length > 0;
  const attention = lines.filter((line) => checks.get(line.key)?.blocking).length;

  const term = lineSearch.trim().toLowerCase();
  const shown = term ? lines.filter((line) => `${line.name} ${line.sku}`.toLowerCase().includes(term)) : lines;

  const patchLine = (key: string, next: (line: CatalogueLine) => CatalogueLine) => setLines((prev) => prev.map((line) => (line.key === key ? next(line) : line)));
  const removeLine = (key: string) => setLines((prev) => prev.filter((line) => line.key !== key));
  const discard = () => {
    setLines(saved);
    setFlagged(false);
  };

  /* ---------------- Actions ---------------- */

  const backToList = () => webNavigate(`/${preLanguage}/supplierInventory`);
  const onBack = () => (dirty ? setConfirm('leave') : backToList());

  const flagAndFocus = () => {
    setFlagged(true);
    window.setTimeout(() => {
      const first = document.querySelector<HTMLElement>('[data-lines] [aria-invalid="true"]');
      first?.scrollIntoView({ block: 'center' });
      first?.focus();
    }, 0);
  };

  const save = async () => {
    if (busy) return;
    if (lines.some((line) => checkLine(line, lines, true).blocking)) {
      flagAndFocus();
      return;
    }
    setBusy(true);
    try {
      const response: any = await saveSupplierCatalogue(id, cataloguePayload(lines, saved));
      apply(response?.data?.data);
      showToast({ message: t('Catalogue saved'), type: 'success', duration: 5000 });
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- Add products ---------------- */

  useDebounceEffect(() => setPickTerm(pickSearch.trim()), [pickSearch], 300);

  const loadPicks = async (page: number, append: boolean) => {
    const mine = ++pickSeq.current;
    setPicksLoading(true);
    try {
      const response: any = await getSupplierCatalogueProducts(id, page, PICK_PAGE, pickTerm, groupId || undefined);
      if (mine !== pickSeq.current) return;
      const rows: any[] = response?.data?.data?.products ?? [];
      rows.forEach((row) => seenPicks.current.set(String(row.id), row));
      setPicks((prev) => {
        if (!append) return rows;
        const known = new Set(prev.map((row) => String(row.id)));
        return [...prev, ...rows.filter((row) => !known.has(String(row.id)))];
      });
      setPickCount(Number(response?.data?.data?.count) || 0);
      setPickPage(page);
    } catch (error: any) {
      if (mine === pickSeq.current) showApiError(error, { language });
    } finally {
      if (mine === pickSeq.current) setPicksLoading(false);
    }
  };

  useEffect(() => {
    if (pickerOpen) loadPicks(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickTerm, groupId]);

  const openPicker = () => {
    setPickSearch('');
    setPickTerm('');
    setGroupId('');
    setSelected(new Map());
    setPickerOpen(true);
    if (!groups.length) {
      getGroupProduct(companyId, 1, 1000, '')
        .then((response: any) => {
          const rows: any[] = response?.data?.productGroupWithCompany ?? response?.data?.data?.productGroupWithCompany ?? [];
          setGroups(rows.filter((row) => row?.id).map((row) => ({ id: String(row.id), name: String(row.groupName ?? '') })));
        })
        .catch(() => undefined);
    }
  };

  // A unit already on the page (saved or just added) is taken; another unit of the same product can still be added.
  const onPage = new Set(lines.map((line) => unitKey(line.productId, line.productUOMId)));
  const pickerRows: PickerRow[] = picks.map((pick) => {
    const productId = String(pick.id);
    const units = unitsOfProduct(pick).map((unit) => ({ id: unit.id, name: unit.name, taken: onPage.has(unitKey(productId, unit.id)) }));
    const free = units.find((unit) => !unit.taken)?.id ?? '';
    const preferred = defaultUnitId(unitsOfProduct(pick));
    return {
      id: productId,
      name: pick?.productName ?? '',
      sku: pick?.sku ?? null,
      group: pick?.productGroup?.groupName ?? null,
      units,
      selected: selected.has(productId),
      unitId: selected.get(productId) || (units.some((unit) => unit.id === preferred && !unit.taken) ? preferred : free),
    };
  });

  const togglePick = (row: PickerRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else if (row.unitId) next.set(row.id, row.unitId);
      return next;
    });
  const pickUnit = (row: PickerRow, unitId: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(row.id, unitId);
      return next;
    });

  const addPicked = () => {
    const keys = new Set(lines.map((line) => line.key));
    const taken = new Set(onPage);
    const added: CatalogueLine[] = [];
    selected.forEach((unitId, productId) => {
      const pick = seenPicks.current.get(productId);
      if (!pick || !unitId || taken.has(unitKey(productId, unitId))) return;
      const line = lineOfPick(pick, unitId, keys);
      keys.add(line.key);
      taken.add(unitKey(productId, unitId));
      added.push(line);
    });
    setLines((prev) => [...prev, ...added]);
    // A just-added row is never hidden by the line search.
    setLineSearch('');
    setPickerOpen(false);
  };

  /* ---------------- Header ---------------- */

  const products = new Set(saved.map((line) => line.productId)).size;
  const discontinued = saved.filter((line) => !line.active).length;
  const facts: Fact[] = [
    { key: 'products', label: t('Products'), value: String(products), lines: [saved.length === 1 ? t('1 price') : t('{count} prices', { count: saved.length })], numeric: true },
    { key: 'discontinued', label: t('Discontinued'), value: discontinued ? String(discontinued) : undefined, pending: t('None'), numeric: true },
  ];

  /* ---------------- Lines card ---------------- */

  const message = (inputId: string, text: string) =>
    text ? (
      <FieldMessage id={`${inputId}-msg`} tone="error">
        {t(text)}
      </FieldMessage>
    ) : null;

  const unitSelect = (line: CatalogueLine, selectId: string) => {
    const error = checks.get(line.key)?.unitError ?? '';
    return (
      <>
        <SelectField
          id={selectId}
          phone={phone}
          value={line.productUOMId}
          invalid={!!error}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${selectId}-msg` : undefined}
          onChange={(event) => patchLine(line.key, (prev) => withUnit(prev, event.target.value))}
          wrapperStyle={phone ? { flex: 'none', width: '128px' } : undefined}
        >
          {!line.productUOMId && <option value="">{t('Choose a unit')}</option>}
          {line.units.map((unit) => (
            // Another row of this product already has the unit.
            <option key={unit.id} value={unit.id} disabled={unit.id !== line.productUOMId && onPage.has(unitKey(line.productId, unit.id))}>
              {unit.name}
            </option>
          ))}
        </SelectField>
        {message(selectId, error)}
      </>
    );
  };

  const priceInput = (line: CatalogueLine, inputId: string, field: 'cost' | 'unitCost') => {
    const check = checks.get(line.key);
    const error = (field === 'cost' ? check?.costError : check?.unitCostError) ?? '';
    const value = field === 'cost' ? line.cost : line.unitCost;
    const label = field === 'cost' ? t('Unit price for {product}', { product: line.name }) : t('Base unit price for {product}', { product: line.name });
    return (
      <>
        <NumberField
          id={inputId}
          column={field}
          phone={phone}
          prefix="€"
          srLabel={phone ? undefined : label}
          value={value}
          invalid={!!error}
          describedBy={error ? `${inputId}-msg` : undefined}
          onChange={(next) => patchLine(line.key, (prev) => (field === 'cost' ? withCost(prev, next) : withUnitCost(prev, next)))}
          onBlur={() => {
            const n = parseNumber(value);
            if (n !== null && Number.isFinite(n)) patchLine(line.key, (prev) => (field === 'cost' ? { ...prev, cost: priceText(n) } : { ...prev, unitCost: priceText(n) }));
          }}
        />
        {message(inputId, error)}
      </>
    );
  };

  const activeBox = (line: CatalogueLine, inputId: string) => (
    <input
      id={inputId}
      className="pu-check"
      type="checkbox"
      checked={line.active}
      aria-label={t('Active for {product}', { product: line.name })}
      onChange={(event) => patchLine(line.key, (prev) => ({ ...prev, active: event.target.checked }))}
      style={{ width: '20px', height: '20px', margin: 0 }}
    />
  );

  const perBase = (line: CatalogueLine) => {
    const base = baseUnitName(line);
    return base ? <span style={{ flex: 'none', fontSize: '13px', lineHeight: '20px', color: MUTED, whiteSpace: 'nowrap' }}>/ {base}</span> : null;
  };

  const desktopTable = () => (
    <div data-lines="" style={{ overflowX: 'auto' }}>
      <table style={tableStyle(840)}>
        <thead>
          <tr>
            <th scope="col" style={th('left', { first: true })}>
              {t('Product')}
            </th>
            <th scope="col" style={th('left', { width: 140 })}>
              {t('Unit')}
            </th>
            <th scope="col" style={th('right', { width: 128 })}>
              {t('Unit price')}
            </th>
            <th scope="col" style={th('right', { width: 176 })}>
              {t('Base unit price')}
            </th>
            <th scope="col" style={th('left', { width: 64, padRight: 0 })}>
              {t('Active')}
            </th>
            <th scope="col" style={th('left', { width: 44, padLeft: 2, padRight: 10 })}>
              <span className="pu-sr">{t('Remove')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((line) => {
            const error = !!checks.get(line.key)?.blocking;
            return (
              <tr key={line.key} className="pu-row">
                <td style={td({ first: true, error })}>
                  <ProductCell name={line.name} sku={line.sku} context={line.group} muted={!line.active} />
                </td>
                <td style={td({ error })}>
                  <label className="pu-sr" htmlFor={`unit-${line.key}`}>
                    {t('Unit for {product}', { product: line.name })}
                  </label>
                  {unitSelect(line, `unit-${line.key}`)}
                </td>
                <td style={td({ align: 'right', error })}>{priceInput(line, `cost-${line.key}`, 'cost')}</td>
                <td style={td({ align: 'right', error })}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>{priceInput(line, `base-${line.key}`, 'unitCost')}</div>
                    <div style={{ paddingTop: '10px' }}>{perBase(line)}</div>
                  </div>
                </td>
                <td style={td({ padRight: 0, padTop: 22, error })}>{activeBox(line, `active-${line.key}`)}</td>
                <td style={td({ align: 'right', padLeft: 2, padRight: 10, padTop: 16, error })}>
                  <IconButton icon="close" remove label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const phoneLines = () => (
    <div data-lines="">
      {shown.map((line) => {
        const nameId = `line-${line.key}`;
        const base = baseUnitName(line);
        return (
          <PhoneLine key={line.key} labelledBy={nameId} error={!!checks.get(line.key)?.blocking}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <ProductCell phone nameId={nameId} name={line.name} sku={line.sku} context={line.group} muted={!line.active} />
              <label className="pu-sr" htmlFor={`m-unit-${line.key}`}>
                {t('Unit for {product}', { product: line.name })}
              </label>
              <div style={{ flex: 'none' }}>{unitSelect(line, `m-unit-${line.key}`)}</div>
              <IconButton icon="close" remove phone label={t('Remove {product}', { product: line.name })} onClick={() => removeLine(line.key)} style={{ marginRight: '-16px' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: '8px', marginTop: '10px' }}>
              <div style={{ minWidth: 0 }}>
                <PhoneLabel htmlFor={`m-cost-${line.key}`}>{t('Unit price')}</PhoneLabel>
                {priceInput(line, `m-cost-${line.key}`, 'cost')}
              </div>
              <div style={{ minWidth: 0 }}>
                <PhoneLabel htmlFor={`m-base-${line.key}`}>{base ? t('Per {unit}', { unit: base }) : t('Base unit price')}</PhoneLabel>
                {priceInput(line, `m-base-${line.key}`, 'unitCost')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <PhoneLabel htmlFor={`m-active-${line.key}`}>{t('Active')}</PhoneLabel>
                <div style={{ height: '44px', display: 'flex', alignItems: 'center' }}>{activeBox(line, `m-active-${line.key}`)}</div>
              </div>
            </div>
          </PhoneLine>
        );
      })}
    </div>
  );

  const linesBody = () => {
    if (loading && !supplier) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: phone ? '16px' : '20px' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      );
    }
    if (lines.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          <EmptyState
            phone={phone}
            title={t('No products in this catalogue yet')}
            desc={t('Use Add products to choose what {supplier} sells, then enter the prices.', { supplier: name || t('this supplier') })}
            action={
              <Button variant="primary" phone={phone} icon="plus" onClick={openPicker}>
                {t('Add products')}
              </Button>
            }
          />
        </div>
      );
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: phone ? '16px' : '20px' }}>
          <EmptyState
            icon="search"
            phone={phone}
            title={t('No products in this catalogue match "{search}". Check the spelling or search by SKU.', { search: lineSearch.trim() })}
            action={
              <Button variant="text" phone={phone} onClick={() => setLineSearch('')}>
                {t('Clear search')}
              </Button>
            }
          />
        </div>
      );
    }
    return phone ? phoneLines() : desktopTable();
  };

  /* ---------------- Save bar ---------------- */

  const changeText = (change: { kind: string; key: string }) => {
    const line = (change.kind === 'removed' ? saved : lines).find((other) => other.key === change.key);
    const label = line?.name ?? '';
    return change.kind === 'added' ? t('Added {name}', { name: label }) : change.kind === 'removed' ? t('Removed {name}', { name: label }) : t('Edited {name}', { name: label });
  };
  const attentionText = attention ? (attention === 1 ? t('1 line needs attention') : t('{count} lines need attention', { count: attention })) : null;
  const saveBar = dirty && (
    <SaveBar
      phone={phone}
      label={t('Unsaved changes')}
      summary={{
        lead: changes.length === 1 ? t('1 unsaved change') : t('{count} unsaved changes', { count: changes.length }),
        detail: [changes.length > 1 ? t('{first}, +{count} more', { first: changeText(changes[0]), count: changes.length - 1 }) : changeText(changes[0])],
        attention: attentionText,
      }}
      secondary={
        !phone ? (
          <Button variant="text" disabled={busy} onClick={discard}>
            {t('Discard changes')}
          </Button>
        ) : undefined
      }
      primary={
        <Button variant="primary" phone={phone} fullWidth={phone} disabled={busy} aria-busy={busy || undefined} onClick={save}>
          {busy ? t('Saving…') : t('Save changes')}
        </Button>
      }
    />
  );

  return (
    <>
      <PageFrame phone={phone} withPhoneBar={!!saveBar}>
        {failed && !supplier ? (
          <ErrorBanner message={t('The catalogue could not be loaded.')} retryLabel={t('Try again')} onRetry={load} />
        ) : (
          <>
            {supplier ? (
              <DocumentHeader
                phone={phone}
                t={t}
                backLabel={t('Back to supplier inventory')}
                onBack={onBack}
                title={name}
                facts={facts}
                moreItems={phone && dirty ? [{ key: 'discard', label: t('Discard changes'), icon: 'undo', onSelect: discard }] : []}
              />
            ) : (
              <section style={{ ...card, display: 'flex', flexDirection: 'column', gap: '16px', padding: phone ? '16px' : '20px' }}>
                <Skeleton width="120px" />
                <Skeleton width="220px" height={28} />
                <Skeleton height={64} />
              </section>
            )}
            <LinesCard
              phone={phone}
              titleId="pu-lines-title"
              title={t('Products')}
              count={supplier ? lines.length : undefined}
              search={lines.length > 0 ? { label: t('Find a product in this catalogue'), placeholder: t('Find a product in this catalogue'), value: lineSearch, onChange: setLineSearch } : undefined}
              actions={
                lines.length > 0 ? (
                  <Button phone={phone} icon="plus" onClick={openPicker}>
                    {t('Add products')}
                  </Button>
                ) : undefined
              }
            >
              {linesBody()}
            </LinesCard>
            {saveBar}
          </>
        )}
      </PageFrame>
      <ProductPicker
        open={pickerOpen}
        phone={phone}
        t={t}
        title={t('Add products to {supplier}', { supplier: name })}
        search={pickSearch}
        onSearch={setPickSearch}
        groups={groups}
        groupId={groupId}
        onGroup={setGroupId}
        rows={pickerRows}
        loading={picksLoading}
        hasMore={picks.length < pickCount}
        onMore={() => loadPicks(pickPage + 1, true)}
        onToggle={togglePick}
        onUnit={pickUnit}
        selectedCount={selected.size}
        onCancel={() => setPickerOpen(false)}
        onAdd={addPicked}
        takenLabel={t('In this catalogue')}
        takenUnitsLabel={(units) => t('In this catalogue: {units}', { units })}
        takenUnitLabel={(unit) => t('{unit} · in this catalogue', { unit })}
        unitLabel={(product) => t('Unit for {product}', { product })}
        searchPlaceholder={t('Name or SKU')}
      />
      <ConfirmDialog
        open={confirm === 'leave'}
        phone={phone}
        danger
        title={t('Leave without saving?')}
        body={t('Your changes to this catalogue are not saved.')}
        confirmLabel={t('Leave without saving')}
        cancelLabel={t('Keep editing')}
        closeLabel={t('Close')}
        onConfirm={() => {
          setConfirm('');
          backToList();
        }}
        onCancel={() => setConfirm('')}
      />
    </>
  );
};

export default SupplierInventoryDetail;
